import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import { Typography } from "@platform/design-system";

import { TIER_COLORS } from "./constants";

import type { CarrierTier } from "../types";

function CardTitle({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Typography variant="header-small" sx={{ whiteSpace: "nowrap" }}>
      <strong>{children}</strong>
    </Typography>
  );
}

function HelpIcon({
  label,
  description,
  ariaLabel,
}: {
  label: string;
  description?: string;
  ariaLabel?: string;
}): React.JSX.Element | null {
  if (!description) return null;

  return (
    <Tooltip title={description}>
      <IconButton
        aria-label={ariaLabel ?? `${label} definition`}
        size="small"
        sx={{ p: 0.25, color: "text.secondary" }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

function HeroStat({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description?: string;
}): React.JSX.Element {
  return (
    <Box>
      <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
        <Typography variant="body-medium" color="textSecondary">
          {label}
        </Typography>
        <HelpIcon label={label} description={description} />
      </Box>
      <Typography>
        <strong>{value}</strong>
      </Typography>
    </Box>
  );
}

function RowStat({
  label,
  value,
  description,
}: {
  label: string;
  value: string | number;
  description?: string;
}): React.JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        minHeight: 20,
      }}
    >
      <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
        <Typography variant="caption" color="textSecondary">
          {label}
        </Typography>
        <HelpIcon label={label} description={description} />
      </Box>
      <Typography variant="caption">
        <strong>{value}</strong>
      </Typography>
    </Box>
  );
}

function LegendKey({
  tier,
  value,
}: {
  tier: CarrierTier;
  value?: string;
}): React.JSX.Element {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: TIER_COLORS[tier],
          flexShrink: 0,
        }}
      />
      <Typography variant="caption" color="textSecondary">
        {tier}:
      </Typography>
      {value && (
        <Typography aria-label={`${tier} legend value`} variant="caption">
          <strong>{value}</strong>
        </Typography>
      )}
    </Box>
  );
}

function TierChip({ tier }: { tier: CarrierTier }): React.JSX.Element {
  if (tier === "Non-Strategic") {
    return (
      <Chip
        label="Non-Strategic"
        size="small"
        variant="outlined"
        sx={{ height: 24, fontSize: "13px" }}
      />
    );
  }
  return (
    <Chip
      label={tier}
      size="small"
      sx={{
        height: 24,
        fontSize: "13px",
        bgcolor: TIER_COLORS[tier],
        color: "#fff",
        "& .MuiChip-label": { px: "6px" },
      }}
    />
  );
}

export { CardTitle, HeroStat, RowStat, LegendKey, TierChip, HelpIcon };
